import { useEffect, useState } from "react";
import { api, formatApiError, mediaUrl } from "../api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Plus, Pencil, Trash2, Calendar as CalIcon, MapPin } from "lucide-react";
import { toast } from "sonner";
import ImagePicker from "../components/ImagePicker";

const empty = {
  title: "",
  description: "",
  date: "",
  time: "",
  location: "",
  image_url: "",
  category: "general",
};

export default function EventsAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/api/admin/events");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const startNew = () => {
    setForm(empty);
    setOpen(true);
  };
  const startEdit = (it) => {
    setForm({ ...empty, ...it });
    setOpen(true);
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    try {
      await api.delete(`/api/admin/events/${id}`);
      setItems((arr) => arr.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const save = async () => {
    if (!form.title || !form.date) {
      toast.error("Title and date are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        description: form.description,
        date: form.date,
        time: form.time,
        location: form.location,
        image_url: form.image_url,
        category: form.category,
      };
      if (form.id) {
        const { data } = await api.put(`/api/admin/events/${form.id}`, payload);
        setItems((arr) => arr.map((x) => (x.id === data.id ? data : x)));
      } else {
        const { data } = await api.post("/api/admin/events", payload);
        setItems((arr) => [data, ...arr]);
      }
      setOpen(false);
      toast.success("Saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="events-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-serif font-bold">Events</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage upcoming and past events.</p>
        </div>
        <Button onClick={startNew} className="bg-primary hover:bg-primary-dark text-white" data-testid="new-event-btn">
          <Plus className="w-4 h-4 mr-2" /> New event
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      ) : items.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground">No events yet. Create your first one.</Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((it) => (
            <Card key={it.id} className="overflow-hidden" data-testid={`event-card-${it.id}`}>
              {it.image_url && (
                <img src={mediaUrl(it.image_url)} alt={it.title} className="w-full h-40 object-cover" />
              )}
              <div className="p-4 space-y-2">
                <div className="font-serif font-semibold">{it.title}</div>
                <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><CalIcon className="w-3 h-3" />{it.date} {it.time}</span>
                  {it.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{it.location}</span>}
                </div>
                <p className="text-sm text-muted-foreground line-clamp-2">{it.description}</p>
                <div className="flex gap-2 pt-2">
                  <Button size="sm" variant="outline" onClick={() => startEdit(it)} data-testid={`edit-event-${it.id}`}>
                    <Pencil className="w-3 h-3 mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="outline" className="text-destructive hover:text-destructive" onClick={() => remove(it.id)} data-testid={`delete-event-${it.id}`}>
                    <Trash2 className="w-3 h-3 mr-1" /> Delete
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{form.id ? "Edit event" : "New event"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Title *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="event-title-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Date *</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="event-date-input" />
              </div>
              <div>
                <Label>Time</Label>
                <Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Location</Label>
              <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
            </div>
            <div>
              <Label>Category</Label>
              <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="e.g. Workshop, Festival" />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <ImagePicker label="Event image" value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} testId="event-image" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-primary hover:bg-primary-dark text-white" data-testid="save-event-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
